import { useQuery } from '@tanstack/react-query'
import type { ProductTranslationCompleteness } from '@today/types'
import { EmptyState } from '@today/ui-components'
import { ProductFormView, ProductImageView, ProductLifecycleView, ProductTranslationsView } from '@today/ui-domain'
import { Tabs } from '@today/ui-primitives'
import { useTranslation } from 'react-i18next'
import { useParams, useSearchParams } from 'react-router-dom'
import { contentCapabilities } from '@/auth/content-capabilities'
import { productCapabilities } from '@/auth/product-capabilities'
import { useViewerPermissions } from '@/auth/useViewerPermissions'
import { productErrorStatus } from '@/hooks/products/product-ui'
import { productKey, useProductEditor } from '@/hooks/products/useProductEditor'
import { useProductImage } from '@/hooks/products/useProductImage'
import { useProductLifecycle } from '@/hooks/products/useProductLifecycle'
import { useProductTranslations, useTranslationsQuery } from '@/hooks/products/useProductTranslations'
import { useTenantLocales } from '@/hooks/products/useTenantLocales'
import { ProductLegalTextsTab } from '@/pages/Content/ProductLegalTextsTab'
import { ForbiddenPage } from '@/pages/errors/Forbidden'
import type { ProductDTO } from '@/services/product.service'
import { productService } from '@/services/product.service'

function BaseForm({ product }: { product?: ProductDTO }) {
	return <ProductFormView {...useProductEditor(product)} />
}

function ProductSections(props: {
	product: ProductDTO
	enabledLocales: string[]
	completeness: ProductTranslationCompleteness | undefined
}) {
	const { product, enabledLocales, completeness } = props
	const caps = productCapabilities(useViewerPermissions().permissions)
	return (
		<div className="flex flex-col gap-8">
			<ProductLifecycleView {...useProductLifecycle(product, completeness, caps)} />
			<BaseForm product={product} />
			<ProductImageView {...useProductImage(product, caps)} />
			<ProductTranslationsView {...useProductTranslations(product, enabledLocales, caps)} />
		</div>
	)
}

const LEGAL_TAB = 'legal-texts'

/**
 * The saved product's tabs (TO-420 content-admin-ui REQ-010): "Product" (the
 * TO-416 sections, unchanged, the default) and "Legal texts" (with
 * `content.read`). The selected tab lives in the `tab` query parameter.
 */
function LoadedProduct(props: {
	product: ProductDTO
	enabledLocales: string[]
	completeness: ProductTranslationCompleteness | undefined
}) {
	const { t } = useTranslation('content')
	const [search, setSearch] = useSearchParams()
	const canReadContent = contentCapabilities(useViewerPermissions().permissions).read
	if (!canReadContent) return <ProductSections {...props} />
	const selected = search.get('tab') === LEGAL_TAB ? LEGAL_TAB : 'product'
	return (
		<Tabs
			aria-label={t('legal.sections')}
			selectedKey={selected}
			onSelectionChange={(key) => {
				const next = new URLSearchParams(search)
				if (key === LEGAL_TAB) next.set('tab', LEGAL_TAB)
				else next.delete('tab')
				setSearch(next)
			}}
			tabs={[
				{
					id: 'product',
					label: t('legal.productTab'),
					content: selected === 'product' ? <ProductSections {...props} /> : null,
				},
				{
					id: LEGAL_TAB,
					label: t('legal.tab'),
					content: selected === LEGAL_TAB ? <ProductLegalTextsTab productId={props.product.productId} /> : null,
				},
			]}
		/>
	)
}

/**
 * Renders once the tab set (tenant locales) and completeness are known, so the
 * tabs and the publish gate never flash an incomplete state.
 */
function ExistingProduct({ product }: { product: ProductDTO }) {
	const { settings, isLoading } = useTenantLocales()
	const translations = useTranslationsQuery(product.productId)
	if (isLoading || translations.isPending) return null
	return (
		<LoadedProduct
			key={product.productId}
			product={product}
			enabledLocales={settings.enabledLocales}
			completeness={translations.data?.completeness}
		/>
	)
}

/**
 * `/products/new` and `/products/:id` — the product editor (TO-416
 * product-admin-ui REQ-003…REQ-007). A new product shows only the base form.
 */
export function ProductEditorPage() {
	const { t } = useTranslation('products')
	const { id } = useParams()
	const query = useQuery({
		queryKey: productKey(id ?? ''),
		queryFn: () => productService.get(id as string),
		enabled: Boolean(id),
		// Mutations write the product into the cache; a fresh entry must not refetch on mount.
		staleTime: 30_000,
	})
	if (!id) return <BaseForm />
	const status = productErrorStatus(query.error)
	if (status === 403) return <ForbiddenPage />
	if (status === 404) return <EmptyState title={t('notFound')} />
	if (query.isError)
		return (
			<EmptyState
				title={t('errors.generic')}
				action={{ label: t('actions.retry'), onPress: () => void query.refetch() }}
			/>
		)
	return query.data ? <ExistingProduct product={query.data} /> : null
}
